import React, { useEffect, useMemo, useState } from 'react';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { useCallback } from 'react';
import { useSimpleReadWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadWritePattern';
import { EntitlementFeature } from 'libs/end-to-end/src/group-entitlements';
import { EntitlementFeatureKey } from 'libs/end-to-end/src/group-entitlements';
import { GroupEntitlementResponse } from 'libs/end-to-end/src/group-entitlements';
import { GroupEntitlements } from 'libs/end-to-end/src/group-entitlements';
import { isEntitlementFeatureWithOptionalFocusArea } from 'libs/end-to-end/src/group-entitlements';
import { SetGroupEntitlementsRequest } from 'libs/end-to-end/src/group-entitlements';
import { validateEntitlementFeatures } from 'libs/end-to-end/src/group-entitlements';
import { OrganizationGroupSelectionWithOrganization } from 'libs/end-to-end/src/group-entitlements';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useParams } from 'react-router';
import { Databases } from 'libs/end-to-end/src/types/databases/databases';
import { compileDataRulePattern } from 'libs/end-to-end/src/data-rules';
import { DataRulePattern } from 'libs/end-to-end/src/data-rules';
import { DataRulePatternSegmentType } from 'libs/end-to-end/src/data-rules';
import { DataRulePatternSegmentWildcardStringExcludingMode } from 'libs/end-to-end/src/data-rules';
import { PatternBuilderPendingSegmentState } from 'apps/sigma2-ui/src/components/permissions/PatternBuilder';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { GroupEntitlement } from 'libs/end-to-end/src/group-entitlements';
import { GroupEntitlementRow } from 'apps/sigma2-ui/src/components/permissions/GroupEntitlementRow';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { CircularProgress } from '@mui/material';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';
import { convertTranslatedContentToReactNode } from 'apps/sigma2-ui/src/lib/hooks/useTranslation';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';

export const GroupEntitlementsPage = () => {
  const language = useLanguage();
  const { groupId } = useParams<{ groupId: string }>();

  const getGroupUrlFn = useCallback(() => {
    if (!groupId) {
      return null;
    }

    return `${getApiBaseUrl()}/groups/${groupId}`;
  }, [groupId]);

  const groupReadPattern = useSimpleReadPattern({
    getUrlFn: getGroupUrlFn,
    canRead: true,
    responseSchema: OrganizationGroupSelectionWithOrganization,
  });

  const isGroupLoading = useMemo(
    () => groupReadPattern.status === 'loading',
    [groupReadPattern.status],
  );
  const isGroupError = useMemo(
    () => groupReadPattern.status === 'error',
    [groupReadPattern.status],
  );

  const group = useMemo(() => {
    if (groupReadPattern.status !== 'done') {
      return null;
    }

    return groupReadPattern.entity;
  }, [groupReadPattern]);

  const organizationId = useMemo(() => {
    if (!group) {
      return null;
    }

    return group.organizationId;
  }, [group]);

  const getDatabasesUrlFn = useCallback(() => {
    if (!organizationId) {
      return null;
    }

    return `${getApiBaseUrl()}/organizations/${organizationId}/databases`;
  }, [organizationId]);

  const databasesReadPattern = useSimpleReadPattern({
    getUrlFn: getDatabasesUrlFn,
    canRead: true,
    responseSchema: Databases,
  });

  const isDatabasesLoading = useMemo(
    () => databasesReadPattern.status === 'loading',
    [databasesReadPattern.status],
  );

  const isDatabasesError = useMemo(
    () => databasesReadPattern.status === 'error',
    [databasesReadPattern.status],
  );

  const databases = useMemo(() => {
    if (databasesReadPattern.status !== 'done') {
      return null;
    }

    return databasesReadPattern.entity.databases;
  }, [databasesReadPattern]);

  const getDatabaseName = useCallback(
    (databaseId: string) => {
      const database = databases?.find((db) => db.id === databaseId);

      if (!database) {
        return `${databaseId} (unknown)`;
      }

      return database.name;
    },
    [databases],
  );

  const getEntitlementsUrlFn = useCallback(() => {
    if (!groupId) {
      return null;
    }

    return `${getApiBaseUrl()}/groups/${groupId}/entitlements`;
  }, [groupId]);

  const entitlementPattern = useSimpleReadWritePattern({
    canRead: group !== null,
    canWrite: group !== null,
    responseSchema: GroupEntitlementResponse,
    getReadUrlFn: getEntitlementsUrlFn,
    getWriteUrlFn: getEntitlementsUrlFn,
    writeSchema: SetGroupEntitlementsRequest,
    writeMethod: 'PUT',
  });

  const isEntitlementsLoading = useMemo(
    () => entitlementPattern.status === 'loading',
    [entitlementPattern.status],
  );

  const isEntitlementsError = useMemo(
    () => entitlementPattern.status === 'error',
    [entitlementPattern.status],
  );

  useEffect(() => {
    if (entitlementPattern.status === 'done') {
      const newEnt = entitlementPattern.entity.entitlements.map(
        (entitlement) => {
          return {
            databaseId: entitlement.databaseId,
            dataRules: entitlement.dataRules.map((dataRule) => {
              return {
                headerPattern: dataRule.rule?.headerPattern ?? [],
                valuePattern: dataRule.rule?.valuePattern ?? [],
              };
            }),
            features: entitlement.features.map((feature) => {
              return {
                key: feature.key,
                focusArea: isEntitlementFeatureWithOptionalFocusArea(
                  feature.key,
                )
                  ? feature.focusArea
                  : null,
              };
            }),
          };
        },
      );

      setEntitlements(newEnt);
    }
  }, [entitlementPattern]);

  const [entitlements, setEntitlements] = useState<GroupEntitlements>([]);

  const [newEntitlementDatabaseId, setNewEntitlementDatabaseId] =
    React.useState<string>('');

  /* --------------------------------------------------------------------- */
  /*                     Top‑level entitlement state                        */
  /* --------------------------------------------------------------------- */
  const [pendingFeature, setPendingFeature] =
    React.useState<EntitlementFeature>({
      key: 'biasAnalysis',
      focusArea: null,
    });

  /** A copy of the entitlement currently being edited, or `null` when nothing is open. */
  const [draftEntitlement, setDraftEntitlement] = React.useState<{
    index: number;
    draftEntitlement: GroupEntitlement;
  } | null>(null);

  /**
   * When the user opens the Pattern‑Builder for an individual rule we keep a draft copy here
   * so that modifications are isolated until they explicitly "Apply".
   */
  const [currentlyEditingDataRule, setCurrentlyEditingDataRule] =
    React.useState<{
      index: number;
      part: 'header' | 'value';
      draftPattern: DataRulePattern;
    } | null>(null);

  /**
   * While a *segment* of a pattern is being edited we track that separately so that we can
   * discard changes or commit them atomically back into the pattern.
   */
  const [pendingSegmentState, setPendingSegmentState] =
    React.useState<PatternBuilderPendingSegmentState | null>(null);

  const allDatabaseIds = useMemo(() => {
    if (!databases) {
      return [];
    }
    return databases.map((db) => db.id);
  }, [databases]);

  const unusedDatabaseIds = useMemo(() => {
    return allDatabaseIds.filter(
      (id) => !entitlements.some((e) => e.databaseId === id),
    );
  }, [allDatabaseIds, entitlements]);

  const canAddEntitlement = useMemo(() => {
    return draftEntitlement === null && unusedDatabaseIds.length > 0;
  }, [draftEntitlement, unusedDatabaseIds.length]);

  const onAddEntitlement = useCallback(() => {
    if (!newEntitlementDatabaseId) {
      return;
    }
    setEntitlements((prev) => [
      ...prev,
      {
        databaseId: newEntitlementDatabaseId,
        dataRules: [],
        features: [],
      },
    ]);
    setNewEntitlementDatabaseId('');
  }, [newEntitlementDatabaseId]);

  /* --------------------------------------------------------------------- */
  /*                      Entitlement‑level callbacks                       */
  /* --------------------------------------------------------------------- */
  const onEditEntitlement = useCallback(
    (index: number) => {
      const entitlement = entitlements[index];
      if (!entitlement) {
        return;
      }

      // Work on a *copy* so that in‑flight edits are isolated
      setDraftEntitlement({
        index,
        draftEntitlement: { ...entitlement },
      });
    },
    [entitlements],
  );

  const onApplyEntitlementChanges = useCallback(() => {
    setEntitlements((prev) => {
      if (!draftEntitlement) {
        return prev;
      }
      const next = [...prev];
      next[draftEntitlement.index] = draftEntitlement.draftEntitlement;
      return next;
    });
    setDraftEntitlement(null);
  }, [draftEntitlement]);

  const onDiscardEntitlementChanges = useCallback(() => {
    setDraftEntitlement(null);
  }, []);

  /* --------------------------------------------------------------------- */
  /*                     Data‑rule‑level callbacks                          */
  /* --------------------------------------------------------------------- */
  /** The user may only edit a *second* rule once they have finished editing the first. */
  const canEditDataRule = useMemo(
    () => draftEntitlement !== null && currentlyEditingDataRule === null,
    [draftEntitlement, currentlyEditingDataRule],
  );

  const onAddDataRule = useCallback(() => {
    setDraftEntitlement((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        draftEntitlement: {
          ...prev.draftEntitlement,
          dataRules: [
            ...prev.draftEntitlement.dataRules,
            {
              headerPattern: [],
              valuePattern: [],
            },
          ],
        },
      };
    });
  }, []);

  const onEditDataRule = useCallback(
    (index: number, part: 'header' | 'value') => {
      if (!draftEntitlement || !canEditDataRule) {
        return;
      }
      if (
        index < 0 ||
        index >= draftEntitlement.draftEntitlement.dataRules.length
      ) {
        return;
      }

      const pattern =
        part === 'header'
          ? draftEntitlement.draftEntitlement.dataRules[index].headerPattern
          : draftEntitlement.draftEntitlement.dataRules[index].valuePattern;

      // Take a shallow copy so we can mutate freely
      setCurrentlyEditingDataRule({
        index,
        part,
        draftPattern: [...pattern],
      });
    },
    [canEditDataRule, draftEntitlement],
  );

  const onDeleteDataRule = useCallback(
    (index: number) => {
      setDraftEntitlement((prev) => {
        if (!prev || !canEditDataRule) return prev;
        if (index < 0 || index >= prev.draftEntitlement.dataRules.length)
          return prev;

        return {
          ...prev,
          draftEntitlement: {
            ...prev.draftEntitlement,
            dataRules: [
              ...prev.draftEntitlement.dataRules.slice(0, index),
              ...prev.draftEntitlement.dataRules.slice(index + 1),
            ],
          },
        };
      });
    },
    [canEditDataRule],
  );

  /**
   * Commit the *currently editing* rule back into the draft entitlement.
   */
  const onApplyDataRuleChanges = useCallback(() => {
    setDraftEntitlement((prev) => {
      if (!prev || !currentlyEditingDataRule) return prev;
      const { index, part, draftPattern } = currentlyEditingDataRule;
      if (index < 0 || index >= prev.draftEntitlement.dataRules.length)
        return prev;

      const updatedRule = { ...prev.draftEntitlement.dataRules[index] };
      if (part === 'header') {
        updatedRule.headerPattern = draftPattern;
      } else {
        updatedRule.valuePattern = draftPattern;
      }

      return {
        ...prev,
        draftEntitlement: {
          ...prev.draftEntitlement,
          dataRules: [
            ...prev.draftEntitlement.dataRules.slice(0, index),
            updatedRule,
            ...prev.draftEntitlement.dataRules.slice(index + 1),
          ],
        },
      };
    });
    setCurrentlyEditingDataRule(null);
  }, [currentlyEditingDataRule]);

  const onDiscardDataRuleChanges = useCallback(() => {
    setCurrentlyEditingDataRule(null);
  }, []);

  /* --------------------------------------------------------------------- */
  /*                      Pattern‑builder callbacks                         */
  /* --------------------------------------------------------------------- */
  /** Remove a segment at the given index from the in‑memory *draft* pattern. */
  const onRemoveSegment = useCallback((segmentIndex: number) => {
    setCurrentlyEditingDataRule((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        draftPattern: prev.draftPattern.filter((_, i) => i !== segmentIndex),
      };
    });
  }, []);

  /** Start adding a *new* segment – we keep it in pending state until Apply/Cancel */
  const onAddSegment = useCallback(() => {
    if (!currentlyEditingDataRule) {
      return;
    }
    setPendingSegmentState({
      index: currentlyEditingDataRule.draftPattern.length,
      segment: {
        type: 'text',
        text: [],
      },
    });
  }, [currentlyEditingDataRule]);

  /**
   * Commit the *pattern* (not just a single segment) from the local draft back into the
   * draft entitlement.
   */
  const onApplyPatternChanges = useCallback(() => {
    if (!currentlyEditingDataRule) {
      return;
    }
    const { index, part, draftPattern } = currentlyEditingDataRule;

    setDraftEntitlement((prev) => {
      if (!prev) return prev;
      if (index < 0 || index >= prev.draftEntitlement.dataRules.length)
        return prev;

      const updatedRule = { ...prev.draftEntitlement.dataRules[index] };
      if (part === 'header') {
        updatedRule.headerPattern = draftPattern;
      } else {
        updatedRule.valuePattern = draftPattern;
      }
      return {
        ...prev,
        draftEntitlement: {
          ...prev.draftEntitlement,
          dataRules: [
            ...prev.draftEntitlement.dataRules.slice(0, index),
            updatedRule,
            ...prev.draftEntitlement.dataRules.slice(index + 1),
          ],
        },
      };
    });
  }, [currentlyEditingDataRule]);

  /** Revert pattern edits back to the source of truth stored in draftEntitlement. */
  const onDiscardPatternChanges = useCallback(() => {
    if (!draftEntitlement || !currentlyEditingDataRule) {
      return;
    }

    const { index, part } = currentlyEditingDataRule;
    const authoritativePattern =
      part === 'header'
        ? draftEntitlement.draftEntitlement.dataRules[index].headerPattern
        : draftEntitlement.draftEntitlement.dataRules[index].valuePattern;

    setCurrentlyEditingDataRule((prev) =>
      prev ? { ...prev, draftPattern: [...authoritativePattern] } : prev,
    );
    setPendingSegmentState(null);
  }, [draftEntitlement, currentlyEditingDataRule]);

  /** Commit the *currently edited segment* into the draftPattern. */
  const onApplySegmentChanges = useCallback(() => {
    if (!currentlyEditingDataRule || !pendingSegmentState) {
      return;
    }
    const { index: segmentIndex, segment } = pendingSegmentState;

    /* First update the *local* draft pattern */
    setCurrentlyEditingDataRule((prev) => {
      if (!prev) {
        return prev;
      }
      const newPattern = [...prev.draftPattern];
      newPattern[segmentIndex] = segment;
      return { ...prev, draftPattern: newPattern };
    });

    setPendingSegmentState(null);
  }, [currentlyEditingDataRule, pendingSegmentState]);

  const onDiscardSegmentChanges = useCallback(() => {
    setPendingSegmentState(null);
  }, []);

  /* --------------------------- Segment editing --------------------------- */
  const onChangeSegmentType = useCallback(
    (newType: DataRulePatternSegmentType) => {
      setPendingSegmentState((prev) => {
        if (!prev || prev.segment.type === newType) return prev;

        const base = { index: prev.index } as const;

        switch (newType) {
          case 'text':
            return {
              ...base,
              segment: { type: 'text', text: [] },
            };
          case 'wildcard-string':
            return {
              ...base,
              segment: {
                type: 'wildcard-string',
                excludingMode: 'nothing',
                excludingCharacters: '',
                excludingCharactersIsCaseSensitive: false,
                excludingText: [],
              },
            };
          case 'wildcard-number':
            return {
              ...base,
              segment: {
                type: 'wildcard-number',
                minimum: undefined,
                maximum: undefined,
                allowNanAndInfinity: false,
              },
            };
          case 'wildcard-date':
            return {
              ...base,
              segment: {
                type: 'wildcard-date',
                minimum: undefined,
                maximum: undefined,
              },
            };
          case 'whitespace':
            return {
              ...base,
              segment: { type: 'whitespace' },
            };
          default:
            return prev;
        }
      });
    },
    [],
  );

  /* Text segment helpers */
  const onAddTextToSegmentText = useCallback(
    ({ text, isCaseSensitive }: { text: string; isCaseSensitive: boolean }) => {
      setPendingSegmentState((prev) => {
        if (!prev || prev.segment.type !== 'text') return prev;
        return {
          ...prev,
          segment: {
            ...prev.segment,
            text: [...prev.segment.text, { text, isCaseSensitive }],
          },
        };
      });
    },
    [],
  );

  const onRemoveTextFromSegmentText = useCallback((index: number) => {
    setPendingSegmentState((prev) => {
      if (!prev || prev.segment.type !== 'text') return prev;
      return {
        ...prev,
        segment: {
          ...prev.segment,
          text: [
            ...prev.segment.text.slice(0, index),
            ...prev.segment.text.slice(index + 1),
          ],
        },
      };
    });
  }, []);

  /* Wildcard‑string helpers */
  const onAddExcludingTextToSegmentWildcardString = useCallback(
    ({ text, isCaseSensitive }: { text: string; isCaseSensitive: boolean }) => {
      setPendingSegmentState((prev) => {
        if (!prev || prev.segment.type !== 'wildcard-string') return prev;
        return {
          ...prev,
          segment: {
            ...prev.segment,
            excludingText: [
              ...prev.segment.excludingText,
              { text, isCaseSensitive },
            ],
          },
        };
      });
    },
    [],
  );

  const onRemoveExcludingTextFromSegmentWildcardString = useCallback(
    (index: number) => {
      setPendingSegmentState((prev) => {
        if (!prev || prev.segment.type !== 'wildcard-string') return prev;
        return {
          ...prev,
          segment: {
            ...prev.segment,
            excludingText: [
              ...prev.segment.excludingText.slice(0, index),
              ...prev.segment.excludingText.slice(index + 1),
            ],
          },
        };
      });
    },
    [],
  );

  const onCharactersChangeForSegmentWildcardStringExcludingModeCharacters =
    useCallback((newValue: string) => {
      setPendingSegmentState((prev) => {
        if (!prev || prev.segment.type !== 'wildcard-string') return prev;
        return {
          ...prev,
          segment: { ...prev.segment, excludingCharacters: newValue },
        };
      });
    }, []);

  const onCaseSensitivityChangeForSegmentWildcardStringExcludingModeCharacters =
    useCallback((newValue: boolean) => {
      setPendingSegmentState((prev) => {
        if (!prev || prev.segment.type !== 'wildcard-string') return prev;
        return {
          ...prev,
          segment: {
            ...prev.segment,
            excludingCharactersIsCaseSensitive: newValue,
          },
        };
      });
    }, []);

  const onExcludingModeChangeForSegmentWildcardString = useCallback(
    (newValue: DataRulePatternSegmentWildcardStringExcludingMode) => {
      setPendingSegmentState((prev) => {
        if (!prev || prev.segment.type !== 'wildcard-string') return prev;
        return {
          ...prev,
          segment: { ...prev.segment, excludingMode: newValue },
        };
      });
    },
    [],
  );

  /* Wildcard‑number helpers */
  const onMinimumChangeForSegmentWildcardNumber = useCallback(
    (newValue: number | undefined) => {
      setPendingSegmentState((prev) => {
        if (!prev || prev.segment.type !== 'wildcard-number') return prev;
        return {
          ...prev,
          segment: { ...prev.segment, minimum: newValue },
        };
      });
    },
    [],
  );

  const onMaximumChangeForSegmentWildcardNumber = useCallback(
    (newValue: number | undefined) => {
      setPendingSegmentState((prev) => {
        if (!prev || prev.segment.type !== 'wildcard-number') return prev;
        return {
          ...prev,
          segment: { ...prev.segment, maximum: newValue },
        };
      });
    },
    [],
  );

  const onAllowNanAndInfinityChangeForSegmentWildcardNumber = useCallback(
    (newValue: boolean) => {
      setPendingSegmentState((prev) => {
        if (!prev || prev.segment.type !== 'wildcard-number') return prev;
        return {
          ...prev,
          segment: { ...prev.segment, allowNanAndInfinity: newValue },
        };
      });
    },
    [],
  );

  /* Wildcard‑date helpers */
  const onMinimumChangeForSegmentWildcardDate = useCallback(
    (newValue: string | undefined) => {
      setPendingSegmentState((prev) => {
        if (!prev || prev.segment.type !== 'wildcard-date') return prev;
        return {
          ...prev,
          segment: { ...prev.segment, minimum: newValue },
        };
      });
    },
    [],
  );

  const onMaximumChangeForSegmentWildcardDate = useCallback(
    (newValue: string | undefined) => {
      setPendingSegmentState((prev) => {
        if (!prev || prev.segment.type !== 'wildcard-date') return prev;
        return {
          ...prev,
          segment: { ...prev.segment, maximum: newValue },
        };
      });
    },
    [],
  );

  /* --------------------------------------------------------------------- */
  /*                         Derived booleans                               */
  /* --------------------------------------------------------------------- */
  const canApplyDraftDataRuleChanges = useMemo(() => {
    if (!currentlyEditingDataRule) {
      return false;
    }

    const { draftPattern } = currentlyEditingDataRule;
    const compilation = compileDataRulePattern(draftPattern);
    return compilation.resultType === 'ok';
  }, [currentlyEditingDataRule]);

  const allDraftRulesCompile = useMemo(() => {
    if (!draftEntitlement) {
      return false;
    }
    return draftEntitlement.draftEntitlement.dataRules.every((rule) => {
      const headerOk =
        compileDataRulePattern(rule.headerPattern).resultType === 'ok';
      const valueOk =
        compileDataRulePattern(rule.valuePattern).resultType === 'ok';
      return headerOk && valueOk;
    });
  }, [draftEntitlement]);

  const allDraftFeaturesValid = useMemo(() => {
    if (!draftEntitlement) {
      return false;
    }

    const validation = validateEntitlementFeatures(
      draftEntitlement.draftEntitlement.features,
    );
    return validation.resultType === 'ok';
  }, [draftEntitlement]);

  /* --------------------------------------------------------------------- */
  /*                     Helpers for the presentation                       */
  /* --------------------------------------------------------------------- */
  /* --------------------------------------------------------------------- */
  /*                     Helpers for the presentation                       */
  /* --------------------------------------------------------------------- */
  const getDataRulesForEntitlement = useCallback(
    (index: number) => {
      // When we’re not editing this entitlement, fall straight through.
      if (!draftEntitlement || draftEntitlement.index !== index) {
        return entitlements[index]?.dataRules ?? [];
      }

      // Otherwise, overlay in‑flight edits.
      return draftEntitlement.draftEntitlement.dataRules.map(
        (rule, ruleIndex) => {
          const maybeReplacePattern = (
            part: 'header' | 'value',
          ): DataRulePattern => {
            /* Not the rule or not the part that’s being edited → keep original. */
            if (
              !currentlyEditingDataRule ||
              currentlyEditingDataRule.index !== ruleIndex ||
              currentlyEditingDataRule.part !== part
            ) {
              return part === 'header' ? rule.headerPattern : rule.valuePattern;
            }

            /* This is the part currently being edited. */
            let pattern = currentlyEditingDataRule.draftPattern;

            /* If a segment is also open for editing, project that preview too. */
            if (pendingSegmentState) {
              const next = [...pattern];
              next[pendingSegmentState.index] = pendingSegmentState.segment;
              pattern = next;
            }
            return pattern;
          };

          /* IMPORTANT: spread `rule` so we don’t lose any extra fields. */
          return {
            ...rule,
            headerPattern: maybeReplacePattern('header'),
            valuePattern: maybeReplacePattern('value'),
          };
        },
      );
    },
    [
      draftEntitlement,
      entitlements,
      currentlyEditingDataRule,
      pendingSegmentState,
    ],
  );

  const getFeaturesForEntitlement = useCallback(
    (index: number) => {
      // When we’re not editing this entitlement, fall straight through.
      if (!draftEntitlement || draftEntitlement.index !== index) {
        return entitlements[index]?.features ?? [];
      }

      // Otherwise, overlay in‑flight edits.
      return draftEntitlement.draftEntitlement.features;
    },
    [draftEntitlement, entitlements],
  );

  const onEditSegment = useCallback(
    (segmentIndex: number) => {
      if (
        !currentlyEditingDataRule ||
        segmentIndex < 0 ||
        segmentIndex >= currentlyEditingDataRule.draftPattern.length
      ) {
        return;
      }
      setPendingSegmentState({
        index: segmentIndex,
        segment: currentlyEditingDataRule.draftPattern[segmentIndex],
      });
    },
    [currentlyEditingDataRule],
  );

  const onDeleteFeature = useCallback((index: number) => {
    setDraftEntitlement((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        draftEntitlement: {
          ...prev.draftEntitlement,
          features: [
            ...prev.draftEntitlement.features.slice(0, index),
            ...prev.draftEntitlement.features.slice(index + 1),
          ],
        },
      };
    });
  }, []);

  const onAddFeature = useCallback(() => {
    setDraftEntitlement((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        draftEntitlement: {
          ...prev.draftEntitlement,
          features: [
            ...prev.draftEntitlement.features,
            {
              key: pendingFeature.key,
              focusArea: isEntitlementFeatureWithOptionalFocusArea(
                pendingFeature.key,
              )
                ? pendingFeature.focusArea
                : null,
            },
          ],
        },
      };
    });
  }, [pendingFeature]);

  const onDeleteEntitlement = useCallback((index: number) => {
    setEntitlements((prev) => {
      if (index < 0 || index >= prev.length) {
        return prev;
      }
      return [...prev.slice(0, index), ...prev.slice(index + 1)];
    });
  }, []);

  const onChangePendingFeatureKey = useCallback(
    (newKey: EntitlementFeatureKey) => {
      setPendingFeature((prev) => ({ ...prev, key: newKey }));
    },
    [],
  );

  const onChangePendingFeatureFocusArea = useCallback(
    (newFocusArea: FocusArea | null) => {
      setPendingFeature((prev) => ({ ...prev, focusArea: newFocusArea }));
    },
    [],
  );

  // Allow user to save if there are no pending changes
  const canSaveEntitlements = useMemo(() => {
    return (
      currentlyEditingDataRule === null &&
      draftEntitlement === null &&
      pendingSegmentState === null
    );
  }, [currentlyEditingDataRule, draftEntitlement, pendingSegmentState]);

  const isSavingEntitlements = useMemo(() => {
    return entitlementPattern.status === 'saving';
  }, [entitlementPattern.status]);

  const [saveError, setSaveError] = useState<BaseError | null>(null);

  const onSaveEntitlement = useCallback(() => {
    if (
      !groupId ||
      (entitlementPattern.status !== 'done' &&
        entitlementPattern.status !== 'error')
    ) {
      return;
    }

    setSaveError(null);

    entitlementPattern.saveEntity(
      {
        entitlements: entitlements.map((entitlement) => {
          return {
            databaseId: entitlement.databaseId,
            organizationGroupId: groupId,
            dataRules: entitlement.dataRules.map((dataRule) => {
              return {
                rule: {
                  headerPattern: dataRule.headerPattern,
                  valuePattern: dataRule.valuePattern,
                },
              };
            }),
            features: entitlement.features.map((feature) => {
              return {
                key: feature.key,
                focusArea: feature.focusArea,
              };
            }),
          };
        }),
      },
      () => {
        document.location.reload();
      },
      (error) => {
        setSaveError(error);
      },
    );
  }, [entitlementPattern, entitlements, groupId]);

  const isLoading = useMemo(() => {
    return isGroupLoading || isDatabasesLoading || isEntitlementsLoading;
  }, [isDatabasesLoading, isEntitlementsLoading, isGroupLoading]);

  const isError = useMemo(() => {
    return isGroupError || isDatabasesError || isEntitlementsError;
  }, [isDatabasesError, isEntitlementsError, isGroupError]);

  /* --------------------------------------------------------------------- */
  /*                               Render                                   */
  /* --------------------------------------------------------------------- */
  return (
    <AdminLayout currentPage={'GroupEntitlements'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Groups', to: '/groups' },
          {
            label: language.groupEntitlementsTitle(),
            to: `/groups/${groupId}/entitlements`,
          },
        ]}
      />
      {isLoading && (
        <CircularProgress
          color="inherit"
          className="text-secondary"
          size={96}
          thickness={8}
        />
      )}
      {!isLoading && isError && (
        <S2Typography
          variant={'headline-7-alt'}
          translatedContent={language.unknownError()}
        />
      )}
      {!isLoading && !isError && (
        <>
          <Spacing size={'md'} direction={'vertical'}>
            <div className={'flex flex-row justify-between'}>
              <S2Typography
                variant={'headline-4-alt'}
                translatedContent={language.groupEntitlementsTitle()}
              />
              <Spacing size={'md'} direction={'horizontal'}>
                {canAddEntitlement && (
                  <>
                    <SelectBoxWithSearch
                      type="single"
                      label={getTranslatedContentAsString(
                        language.selectDatabaseLabel(),
                      )}
                      list={unusedDatabaseIds.map((dbId) => {
                        const database = databases?.find(
                          (db) => db.id === dbId,
                        );

                        return {
                          optionLabel: database?.name ?? '',
                          optionValue: dbId,
                        };
                      })}
                      selected={newEntitlementDatabaseId}
                      setSelected={setNewEntitlementDatabaseId}
                    />
                    <Button
                      variant={'outline'}
                      behavior={{
                        type: 'onClick',
                        onClick: () => {
                          onAddEntitlement();
                        },
                      }}
                      disabled={!newEntitlementDatabaseId}
                    >
                      {convertTranslatedContentToReactNode(
                        language.addEntitlementButton(),
                      )}
                    </Button>
                  </>
                )}
                {canSaveEntitlements && (
                  <Button
                    variant={'primary'}
                    behavior={{
                      type: 'onClick',
                      onClick: () => {
                        onSaveEntitlement();
                      },
                    }}
                    disabled={isSavingEntitlements}
                  >
                    {convertTranslatedContentToReactNode(
                      language.saveEntitlementsButton(),
                    )}
                  </Button>
                )}
                {!isSavingEntitlements && (
                  <Button
                    behavior={{
                      type: 'href',
                      href: `/groups`,
                    }}
                    variant={'outline'}
                  >
                    {convertTranslatedContentToReactNode(
                      language.backToGroupsButton(),
                    )}
                  </Button>
                )}
              </Spacing>
            </div>
            <S2Typography
              variant={'body-5'}
              translatedContent={language.groupNameWithLabel({
                groupName: group?.name ?? '',
              })}
            />
            <S2Typography
              variant={'body-5'}
              translatedContent={language.organizationNameWithLabel({
                organizationName: group?.organization.name ?? '',
              })}
            />
            {saveError && (
              <S2Typography
                variant={'headline-7-alt'}
                translatedContent={language.errorSavingEntitlements({
                  baseError: saveError,
                })}
              />
            )}
            {entitlements.map((entitlement, entitlementIndex) => {
              const isEditing =
                draftEntitlement !== null &&
                draftEntitlement.index === entitlementIndex;

              return (
                <GroupEntitlementRow
                  key={entitlementIndex}
                  dataRules={getDataRulesForEntitlement(entitlementIndex)}
                  entitlementIndex={entitlementIndex}
                  features={getFeaturesForEntitlement(entitlementIndex)}
                  groupEntitlementCallbacks={{
                    onAddDataRule,
                    onAddFeature,
                    onChangePendingFeatureKey,
                    onChangePendingFeatureFocusArea,
                    onApplyDataRuleChanges,
                    onApplyEntitlementChanges,
                    onDiscardEntitlementChanges,
                    onDeleteDataRule,
                    onDiscardDataRuleChanges,
                    onEditEntitlement,
                    onEditDataRule,
                    onDeleteEntitlement,
                    onDeleteFeature,
                  }}
                  databaseName={getDatabaseName(entitlement.databaseId)}
                  pendingFeature={pendingFeature}
                  showAddDataRuleButton={
                    !isSavingEntitlements &&
                    isEditing &&
                    currentlyEditingDataRule === null &&
                    allDraftRulesCompile
                  }
                  showAddFeatureButton={
                    !isSavingEntitlements &&
                    isEditing &&
                    currentlyEditingDataRule === null &&
                    allDraftFeaturesValid
                  }
                  currentlyEditingDataRule={
                    isEditing ? currentlyEditingDataRule : null
                  }
                  showEditEntitlementButton={
                    !isSavingEntitlements && draftEntitlement === null
                  }
                  showDeleteEntitlementButton={
                    !isSavingEntitlements && draftEntitlement === null
                  }
                  showEditDataRuleButton={
                    !isSavingEntitlements && isEditing && canEditDataRule
                  }
                  canApplyDraftDataRuleChanges={
                    !isSavingEntitlements && canApplyDraftDataRuleChanges
                  }
                  showEntitlementChangeButtons={
                    !isSavingEntitlements &&
                    isEditing &&
                    !currentlyEditingDataRule
                  }
                  canApplyEntitlementChanges={
                    !isSavingEntitlements &&
                    isEditing &&
                    !currentlyEditingDataRule &&
                    allDraftRulesCompile &&
                    allDraftFeaturesValid
                  }
                  showDeleteDataRuleButton={
                    !isSavingEntitlements &&
                    isEditing &&
                    canEditDataRule &&
                    draftEntitlement !== null &&
                    draftEntitlement.draftEntitlement.dataRules.length > 0
                  }
                  showDeleteFeatureButton={
                    !isSavingEntitlements &&
                    isEditing &&
                    canEditDataRule &&
                    draftEntitlement !== null
                  }
                  pendingSegmentIndex={
                    isEditing && pendingSegmentState
                      ? pendingSegmentState.index
                      : -1
                  }
                  patternBuilderCallbacks={{
                    onAddSegment,
                    onRemoveSegment,
                    onApplyPatternChanges,
                    onChangeSegmentType,
                    onApplySegmentChanges,
                    onAddTextToSegmentText,
                    onDiscardSegmentChanges,
                    onRemoveTextFromSegmentText,
                    onDiscardPatternChanges,
                    onEditSegment,
                    onAddExcludingTextToSegmentWildcardString,
                    onRemoveExcludingTextFromSegmentWildcardString,
                    onCharactersChangeForSegmentWildcardStringExcludingModeCharacters,
                    onCaseSensitivityChangeForSegmentWildcardStringExcludingModeCharacters,
                    onExcludingModeChangeForSegmentWildcardString,
                    onMinimumChangeForSegmentWildcardNumber,
                    onMaximumChangeForSegmentWildcardNumber,
                    onAllowNanAndInfinityChangeForSegmentWildcardNumber,
                    onMinimumChangeForSegmentWildcardDate,
                    onMaximumChangeForSegmentWildcardDate,
                  }}
                />
              );
            })}
            {entitlements.length === 0 && (
              <S2Typography
                variant={'headline-5-alt'}
                translatedContent={language.noEntitlements()}
              />
            )}
          </Spacing>
        </>
      )}
    </AdminLayout>
  );
};
